/**
 * Position overlay renderer.
 * Draws active trading position indicators (entry line, PnL badge).
 */

import { DEFAULT_CHART_CONFIG } from '../config/constants';
import type { ThemeConfig, LayoutConfig } from '../config/types';
import type { ActivePosition } from '../types';

import type { SVGSelection, YScale, SelectionsCache } from './types';

/**
 * Draws the active position indicators (Entry line, PnL badge).
 *
 * @param svg - D3 SVG selection
 * @param position - Active trading position (or null)
 * @param currentPrice - Current price value
 * @param yScale - Y-axis scale function
 * @param width - Chart width in pixels
 * @param xCursor - Current cursor X position
 * @param theme - Theme configuration (uses defaults if not provided)
 * @param layout - Layout configuration (uses defaults if not provided)
 * @param cache - Optional cached D3 selections (avoids DOM queries)
 */
export const renderPositionOverlay = (
    svg: SVGSelection,
    position: ActivePosition | null,
    currentPrice: number,
    yScale: YScale,
    width: number,
    xCursor: number,
    theme: ThemeConfig = DEFAULT_CHART_CONFIG.theme,
    layout: LayoutConfig = DEFAULT_CHART_CONFIG.layout,
    cache?: SelectionsCache,
): void => {
    // Use cached selections if available, otherwise query DOM
    const group = cache?.positionOverlay ?? svg.select('.position-overlay');

    if (!position) {
        group.style('display', 'none');
        return;
    }

    group.style('display', 'block');

    const { bullish, bearish, profit } = theme;
    const { cursorLabelOffsetX } = layout;

    const yEntry = yScale(position.entryPrice);
    const yCurrent = yScale(currentPrice);

    // 1. Logic for PnL
    let isProfit = false;
    if (position.type === 'LONG') {
        isProfit = currentPrice >= position.entryPrice;
    } else {
        isProfit = currentPrice <= position.entryPrice;
    }

    const color = isProfit ? profit : bearish;
    const diff = currentPrice - position.entryPrice;
    const percent = ((diff / position.entryPrice) * 100).toFixed(2);
    const prefix = diff > 0 ? '+' : '';

    // Use cached selections for child elements
    const entryLine = cache?.entryLine ?? group.select('.entry-line');
    const badgeGroup = cache?.pnlBadge ?? group.select('.pnl-badge');
    const badgeRect = cache?.pnlBadgeRect ?? badgeGroup.select('rect');
    const badgeText = cache?.pnlBadgeText ?? badgeGroup.select('text');

    // 2. Entry Line
    entryLine
        .attr('y1', yEntry)
        .attr('y2', yEntry)
        .attr('x2', width)
        .attr('stroke', position.type === 'LONG' ? bullish : bearish)
        .attr('stroke-opacity', 0.5);

    // 3. PnL Badge (Attached to cursor)
    // We offset it slightly below the main cursor label
    badgeGroup.attr(
        'transform',
        `translate(${xCursor + cursorLabelOffsetX}, ${yCurrent + 28})`,
    );

    badgeRect
        .attr('fill', color)
        .attr('stroke', color)
        .attr('fill-opacity', 0.2);

    badgeText.text(`${position.type} ${prefix}${percent}%`).attr('fill', color);
};
